/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import { useState } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";

// Icons
import { IoQrCode, IoCopy } from "react-icons/io5";

// QR Code
import { QRCodeSVG } from "qrcode.react";

function ReceiveWidget({ address, onGenerateAddress, loading }) {
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(true);

  const handleCopy = () => {
    if (address) {
      navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Card>
      <VuiBox p={3}>
        <VuiBox display="flex" alignItems="center" mb={3}>
          <VuiBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="48px"
            height="48px"
            borderRadius="12px"
            sx={{
              background: "#00CED1",
            }}
          >
            <IoQrCode size="24px" color="#000000" />
          </VuiBox>
          <VuiBox ml={2}>
            <VuiTypography variant="lg" color="white" fontWeight="bold">
              Receive MYT
            </VuiTypography>
            <VuiTypography variant="button" color="text">
              Your wallet address
            </VuiTypography>
          </VuiBox>
        </VuiBox>

        {/* QR Code Display */}
        {showQR && address && !loading && (
          <VuiBox mb={3} display="flex" justifyContent="center">
            <VuiBox
              p={2}
              sx={{
                background: "#FFFFFF",
                borderRadius: "12px",
                display: "inline-block",
              }}
            >
              <QRCodeSVG
                value={address}
                size={200}
                level="H"
                includeMargin={true}
              />
            </VuiBox>
          </VuiBox>
        )}

        <VuiBox mb={3}>
          <VuiBox mb={1} display="flex" justifyContent="space-between" alignItems="center">
            <VuiTypography variant="caption" color="text" fontWeight="medium">
              Your Address
            </VuiTypography>
            <VuiTypography
              variant="caption"
              color="info"
              sx={{ cursor: "pointer" }}
              onClick={() => setShowQR(!showQR)}
            >
              {showQR ? "Hide QR" : "Show QR"}
            </VuiTypography>
          </VuiBox>
          <VuiBox
            p={2}
            sx={{
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "8px",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              wordBreak: "break-all",
            }}
          >
            {loading ? (
              <VuiTypography variant="button" color="text">
                <Icon sx={{ mr: 1, verticalAlign: "middle" }}>sync</Icon>
                Loading address...
              </VuiTypography>
            ) : address ? (
              <VuiTypography variant="button" color="white" fontFamily="monospace">
                {address}
              </VuiTypography>
            ) : (
              <VuiTypography variant="button" color="text">
                No address available
              </VuiTypography>
            )}
          </VuiBox>
        </VuiBox>

        <VuiBox>
          <VuiButton
            variant="contained"
            color="info"
            fullWidth
            onClick={handleCopy}
            disabled={!address || loading}
            sx={{
              background: "linear-gradient(135deg, #00CED1 0%, #8A2BE2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #00CED1 0%, #8A2BE2 100%)",
              },
            }}
          >
            <IoCopy size="18px" style={{ marginRight: "8px" }} />
            {copied ? "Copied!" : "Copy Address"}
          </VuiButton>
        </VuiBox>

        <VuiBox mt={3} p={2} sx={{ background: "rgba(0, 206, 209, 0.1)", borderRadius: "8px" }}>
          <VuiTypography variant="caption" color="info">
            <Icon sx={{ mr: 1, verticalAlign: "middle", fontSize: "16px" }}>info</Icon>
            Send only MYT to this address. Sending other coins may result in permanent loss.
          </VuiTypography>
        </VuiBox>
      </VuiBox>
    </Card>
  );
}

export default ReceiveWidget;
